import { useTranslation } from 'react-i18next';
import { useQueryClient, useIsMutating, keepPreviousData } from '@tanstack/react-query';
import {
  useRef, useMemo, useCallback, useState,
} from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Helmet } from 'react-helmet-async';
import { useMobileReveal } from '../hooks/useMobileReveal';

// Services
import { useTableFiltersOptimistic, useTableFiltersOptimisticColumnManagement } from '../services/table-filters/tableFiltersOptimistic';
import { TABLE_FILTERS_QUERY_KEY, useTableFilters, useTableFiltersMutation } from '../services/table-filters/tableFiltersQueries';
import { useResetFiltersMutation } from '../services/resetFilters/resetFiltersQueries';
import { useBatchSetAttributeMutation, useDeleteMultipleUrlMutation } from '../services/batch-processing/batchProcessingQueries';
import { useTableData, TABLE_QUERY_KEY } from '../services/table/tableQueries';
import { useExportMutation } from '../services/export/exportQueries';

// Store
import { Popover } from '../store/popover/PopoverContext';

// Hooks
import useStickyTopElems from '../hooks/useStickyTopElems';
import { useContexte } from '../hooks/useContexte';
import { useFormPanelContext } from '../store/formPanel/FormPanelContext';
import useToast from '../components/molecules/Toast/useToast';
import { useSyncScrollWithWindow } from '../hooks/useSyncScrollWithWindow';

// Utils
import { appendSelectedIdsToUrl } from '../utils/url';

// Domains
import useTableGroups from '../domain/table/useTable';
import useTableAccordion from '../domain/tableAccordion/useTableAccordion';
import useTableSearch from '../domain/tableSearch/useTableSearch';
import useConditionalFilters from '../domain/tableConditionalFilter/useConditionalFilters';
import useTableSortBy from '../domain/tableSortBy/useTableSortBy';
import useTablePagination from '../domain/tablePagination/useTablePagination';
import useTableGroupBy from '../domain/tableGroupBy/useTableGroupBy';
import useTablePersonalView from '../domain/tablePersonalView/useTablePersonalView';
import useTableColumnManagement from '../domain/tableColumnManagement/useTableColumnManagement';
import useTableBatchProcessing from '../domain/tableBatchProcessing/useTableBatchProcessing';
import useBatchActionsProgression from '../domain/batchActionsProgression/useBatchActionsProgression';
import { transformSetAttributeMultiple } from '../domain/tableBatchProcessing/tableBatchProcessingUseCase';
import useAddElement from '../domain/addElement/useAddElement';
import { addIdsToAddFormUrls } from '../domain/addElement/addElementUseCase';
import useTableExport from '../domain/tableExport/useTableExport';
import useTableImport from '../domain/tableImport/useTableImport';
import { addIdsToImportUrls } from '../domain/tableImport/tableImportUseCase';
import useGlobalFilters from '../domain/globalFilters/useGlobalFilters';
import { globalFiltersMutationKey } from '../services/globalFilters/globalFiltersQueries';
import { completeTableFiltersForPost } from '../domain/tableFilters/tableFiltersUseCase';
import { TableColumnHoverProvider, useTableColumnHoverDispatchOptional } from '../domain/table/useTableColumnHover';
import { TableRowHoverProvider, useTableRowHoverDispatchOptional } from '../domain/table/useTableRowHover';
import { TableCellHoverPreviewProvider } from '../domain/tableCellHover/useTableCellHoverPreview';
import TableRowHoverActions, { ROW_ACTIONS_GUTTER_WIDTH } from '../components/molecules/TableRowHoverActions/TableRowHoverActions';
import { useTableReloadSkeletonHold } from '../domain/table/useTableReloadSkeletonHold';
import { shouldShowTableDataSkeleton } from '../domain/table/tableReloadSkeletonUseCase';

// Components
import Table from '../components/atoms/Table/Table';
import TableWrapper from '../components/organisms/TableWrapper/TableWrapper';
import styles from '../components/organisms/TableWrapper/TableWrapper.module.css';
import tableViewStyles from './Table.module.css';
import TableSubHeader from '../components/atoms/TableSubHeader/TableSubHeader';
import TableHead from '../components/organisms/TableHead/TableHead';
import Button from '../components/atoms/Button/Button';
import ModalTrigger from '../components/molecules/ModalTrigger/ModalTrigger';
import TableTitle from '../components/molecules/TableTitle/TableTitle';
import CircleLoader from '../components/atoms/CircleLoader/CircleLoader';
import tableTitleStyles from '../components/molecules/TableTitle/TableTitle.module.css';
import TableFilters, {
  TableFiltersGroup,
  TableFiltersToolbar,
} from '../components/molecules/TableFilters/TableFilters';
import IconButton from '../components/atoms/IconButton/IconButton';
import Picto from '../components/atoms/Picto/Picto';
import {
  PopoverContent,
  PopoverTrigger,
} from '../components/atoms/Popover/Popover';
import { SidePanelFooter } from '../components/organisms/SidePanel/SidePanel';
import BatchSidePanel from '../components/molecules/BatchSidePanel/BatchSidePanel';
import RowBatchList from '../components/molecules/RowBatchList/RowBatchList';
import RowBatchCard from '../components/molecules/RowBatchCard/RowBatchCard';
import BatchActionSelect from '../components/molecules/BatchActionSelect/BatchActionSelect';
import BatchActionRender from '../components/molecules/BatchActionRender/BatchActionRender';
import SearchGroupList from '../components/molecules/SearchGroupList/SearchGroupList';
import SearchListItem from '../components/molecules/SearchListItem/SearchListItem';
import { SortByForm } from '../components/organisms/SortByForm/SortByForm';
import Badge from '../components/atoms/Badge/Badge';
import TablePagination from '../components/organisms/TablePagination/TablePagination';
import tablePaginationLayoutStyles from '../components/organisms/TablePagination/TablePagination.module.css';
import TablePaginationPerPage from '../components/molecules/TablePaginationPerPage/TablePaginationPerPage';
import tablePaginationPerPageStyles from '../components/molecules/TablePaginationPerPage/TablePaginationPerPage.module.css';
import TablePaginationPerPageButton from '../components/atoms/TablePaginationPerPageButton/TablePaginationPerPageButton';
import TablePaginationNavigation from '../components/molecules/TablePaginationNavigation/TablePaginationNavigation';
import TablePaginationPages from '../components/molecules/TablePaginationPages/TablePaginationPages';
import CustomVueListGroup, {
  CustomVueListGroupList,
  CustomVueListGroupTitle,
} from '../components/organisms/CustomVueListGroup/CustomVueListGroup';
import ColumnsVisibility from '../components/molecules/ColumnsVisibility/ColumnsVisibility';
import SearchField from '../components/molecules/SearchField/SearchField';
import LabelWithIconAndColor from '../components/molecules/LabelWithIconAndColor/LabelWithIconAndColor';
import HorizontalList from '../components/atoms/HorizontalList/HorizontalList';
import Modal, { ModalHeader } from '../components/molecules/Modal/Modal';
import modalStyles from '../components/molecules/Modal/Modal.module.css';
import DeleteRowsConfirmModal from '../components/molecules/DeleteRowsConfirmModal/DeleteRowsConfirmModal';
import BatchActionProgressionContainer from '../components/organisms/BatchActionProgressionContainer/BatchActionProgressionContainer';
import VirtualizedTableGroups from '../components/atoms/VirtualizedTableGroups/VirtualizedTableGroups';
import VirtualizedStickyHeader from '../components/atoms/VirtualizedStickyHeader/VirtualizedStickyHeader';
import VirtualizedTableGroup from '../components/atoms/VirtualizedTableGroup/VirtualizedTableGroup';
import TableGridGroup from '../components/organisms/TableGridGroup/TableGridGroup';
import TableFindBar from '../components/molecules/TableFindBar/TableFindBar';
import useTableFind, { TableFindProvider } from '../domain/tableFind/useTableFind';
import CellHoverPreviewOverlay from '../components/molecules/TableCells/CellHoverPreviewWrapper/CellHoverPreviewOverlay';
import FloatingMenu, {
  FloatingMenuGroups,
  FloatingMenuGroup,
} from '../components/atoms/FloatingMenu/FloatingMenu';
import floatingMenuStyles from '../components/atoms/FloatingMenu/FloatingMenu.module.css';
import CircleProgressIndicator from '../components/atoms/CircleProgressIndicator/CircleProgressIndicator';
import MobileCollapsible from '../components/molecules/MobileCollapsible/MobileCollapsible';
import ResponsiveActionButton from '../components/molecules/ResponsiveActionButton/ResponsiveActionButton';
import SelectionCount from '../components/molecules/SelectionCount/SelectionCount';
import breakpointStyles from '../styles/breakpoints.module.css';

// Filters
import FilterConditionalFilters from '../components/organisms/FilterConditionalFilters/FilterConditionalFilters';
import FilterPersonalViews from '../components/organisms/FilterPersonalViews/FilterPersonalViews';
import FilterColumnManagement from '../components/organisms/FilterColumnManagement/FilterColumnManagement';
import FilterNavigationSearch from '../components/organisms/FilterNavigationSearch/FilterNavigationSearch';
import FilterSelect from '../components/molecules/FilterSelect/FilterSelect';
import filterSelectStyles from '../components/molecules/FilterSelect/FilterSelect.module.css';

// Mocks
import { useGetElementWidth } from '../hooks/useGetElementWidth';

/**
 * Renders under `TableColumnHoverProvider` only: the dispatch context is not visible to `TableView`
 * itself (the provider wraps children), so the hook cannot run in the view body.
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @returns {JSX.Element}
 */
function VirtualizedTableGroupsWithHoverLeave({
  children,
}) {
  const { clearHoveredLeafIndices } = useTableColumnHoverDispatchOptional();
  const { clearHoveredRow } = useTableRowHoverDispatchOptional();
  const handleMouseLeave = (/** @type {React.MouseEvent<HTMLDivElement>} */ e) => {
    clearHoveredLeafIndices();
    const toHoverBar = e.relatedTarget instanceof Element
      && e.relatedTarget.closest('[class*="table-row-hover-actions"]');
    if (!toHoverBar) clearHoveredRow();
  };
  return (
    <VirtualizedTableGroups onMouseLeave={handleMouseLeave}>
      {children}
    </VirtualizedTableGroups>
  );
}

/**
 * Table view component
 * @param {object} params
 * @param {ApiPage} [params.page] - The page
 * @param {string[]} [params.stickyTopElems] - The elements ids to stick to the top of the table needed to calculate the main height of the table
 * @param {boolean} [params.isInTab=false] - Rendered under a detail page tab (hides the title / subtitle)
 * @returns {JSX.Element} The Table component
 */
function TableView({ page, stickyTopElems = ['page-header'], isInTab = false }) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const { handleOpenModal } = useFormPanelContext();
  const { width: tableWrapperWidth, ref: tableWidthProbeRef } = useGetElementWidth();
  const {
    isOpen: isMobileFiltersOpen,
    onToggleClick: onMobileFiltersToggleClick,
  } = useMobileReveal({ initialOpen: false });
  // Get contexte from URL query parameters
  const contexte = useContexte();

  // Domain - Global Filters
  const {
    globalFiltersKey,
    isGlobalFiltersFetched,
  } = useGlobalFilters({ path: page?.path });

  const isGlobalFiltersMutating = useIsMutating({
    mutationKey: globalFiltersMutationKey(page?.path ?? ''),
  }) > 0;

  // Services - Table Filters
  const {
    data: managerTableFilters,
    isLoading: isManagerTableFiltersLoading,
  } = useTableFilters({ path: page?.path, contexte }, { enabled: !!page?.path });

  // Domain - Table Pagination
  const {
    pageSizeOptions,
    pageNumber,
    setPageNumberFromPageSize,
    goToPage,
    resetPageToFirst,
  } = useTablePagination({
    initialPageSize: managerTableFilters?.pageSize || '25',
    isLoading: isManagerTableFiltersLoading,
  });

  const { mutate: resetView, isPending: isResetViewPending } = useResetFiltersMutation({
    onSuccess: () => {
      resetPageToFirst();
      queryClient.invalidateQueries({ queryKey: [TABLE_FILTERS_QUERY_KEY, page?.path, contexte] });
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEY], type: 'active' });
    },
  });

  // Transform setAttributMultiple from new API format to TransformedBatchActionOption format
  const transformedSetAttributeMultiple = useMemo(
    () => transformSetAttributeMultiple(managerTableFilters?.setAttributMultiple),
    [managerTableFilters?.setAttributMultiple],
  );

  // Domain - Table Search
  const tableSearch = useTableSearch({
    searchOptions: managerTableFilters?.search?.options,
    initialValue: managerTableFilters?.search?.searchValue,
    initialSelectedOptions: managerTableFilters?.search?.selectedOptions,
    isLoading: isManagerTableFiltersLoading,
  });

  // Domain - Table accordion
  const {
    isGroupAccordionOpen,
    toggleGroup,
  } = useTableAccordion();

  // Domain - Table Personal View
  const personalViews = useTablePersonalView({
    personalViews: managerTableFilters?.personalViews,
    isLoading: isManagerTableFiltersLoading,
  });
  const {
    selectedPersonalViewItem,
    popoverPersonalViewIsOpen,
    setPopoverPersonalViewIsOpen,
  } = personalViews;

  // Domain - Table GroupBy
  const {
    groupByOptionsId,
    groupByOptionsGrouped,
    popoverGroupByIsOpen,
    setPopoverGroupByIsOpen,
  } = useTableGroupBy({ groupByOptions: managerTableFilters?.groupBy?.options });

  // Domain - Table Conditional Filters
  const conditionalFilters = useConditionalFilters({
    filters: managerTableFilters?.filters,
    initialValues: managerTableFilters?.filterValues,
    isLoading: isManagerTableFiltersLoading,
  });

  // Domain - Table Filter
  const tableSortBy = useTableSortBy({
    tableFilters: managerTableFilters?.sortBy?.criteria,
    initialValuesFilters: managerTableFilters?.sortBy?.criteriaValues,
    isMultiCriteria: managerTableFilters?.sortBy?.multiCriteria,
    isLoading: isManagerTableFiltersLoading,
  });

  // Services - Table Data
  const {
    data: tableData,
    isLoading: isTableLoading,
    isFetching: isTableFetching,
    isPlaceholderData: isTablePlaceholderData,
    isError: isTableError,
  } = useTableData(
    page?.path,
    {
      pageNumber,
      globalFiltersKey,
      contexte,
    },
    {
      enabled: !!page?.path && isGlobalFiltersFetched && !isGlobalFiltersMutating,
      placeholderData: keepPreviousData,
    },
  );

  const tableDataPath = tableData;
  const isWaitingForGlobalFilters = !!page?.path && !isGlobalFiltersFetched;
  const isTableDataBlocking = isTableLoading || isGlobalFiltersMutating || isWaitingForGlobalFilters;

  // Domain - Table Add Element
  const {
    popoverAddElementIsOpen,
    setPopoverAddElementIsOpen,
    searchValue: addElementSearchValue,
    setSearchValue: setAddElementSearchValue,
    addFormUrlsGrouped,
  } = useAddElement({ addFormUrls: tableData?.addFormUrls });

  /**
   * Handle click on "Add Element" button
   * If there's only one addFormUrl, open the form directly
   * Otherwise, open the popover with the list
   */
  const handleAddElementClick = () => {
    const urls = tableData?.addFormUrls;
    if (urls?.length === 1) {
      // just add an id to the url to respect the type AddFormUrlWithId
      const withIds = addIdsToAddFormUrls(urls);
      handleOpenModal(withIds[0].url);
    } else {
      setPopoverAddElementIsOpen(!popoverAddElementIsOpen);
    }
  };

  // Domain - Table Export
  const {
    popoverExportIsOpen,
    setPopoverExportIsOpen,
    searchValue: exportSearchValue,
    setSearchValue: setExportSearchValue,
    selectedFormatFilter,
    setSelectedFormatFilter,
    exportUrlsGrouped,
    optGroupsList,
  } = useTableExport({ exportUrls: tableData?.exportUrls });

  const {
    popoverImportIsOpen,
    setPopoverImportIsOpen,
    importSearchValue,
    setImportSearchValue,
    importUrlsGrouped,
  } = useTableImport({ importUrls: tableData?.importUrls });

  /**
   * Handle click on "Import" button
   * If there's only one importUrl, open the form directly
   * Otherwise, open the popover with the list
   */
  const handleImportClick = () => {
    const urls = tableData?.importUrls;
    if (urls?.length === 1) {
      const withIds = addIdsToImportUrls(urls);
      handleOpenModal(withIds[0].url);
    } else {
      setPopoverImportIsOpen(!popoverImportIsOpen);
    }
  };

  // Domain - Table Column Management
  const columnManagement = useTableColumnManagement({
    columnFilters: managerTableFilters?.columnFilters,
    isLoading: isManagerTableFiltersLoading,
  });
  const {
    columnFiltersLocal,
    columnsPinnedById,
    popoverColumnsManagementIsOpen,
    setPopoverColumnsManagementIsOpen,
  } = columnManagement;

  // Domain - Table Batch Processing
  const {
    handleCloseBatchProcessing,
    batchProcessingIsOpen,
    setBatchProcessingIsOpen,

    formBatchProcessing: {
      control, handleSubmit, clearErrors, formState: { errors },
    },
    handleCreateNewRowBatchItem,
  } = useTableBatchProcessing(
    { batchProcessingConfirmationLabel: t('sidePanel.confirmClose') },
  );

  // Domain - Batch actions progression (modal state + progress)
  const {
    isBatchProgressModalOpen,
    batchProgressActionItems,
    actionProgressList,
    openProgressModal,
    closeProgressModal,
    updateActionProgress,
  } = useBatchActionsProgression();

  // Mutation - Column Resize (bare POST, no invalidation)
  const {
    mutate: mutateColumnResize,
  } = useTableFiltersMutation(page?.path, {}, contexte);

  const handleColumnResizeEnd = useCallback(
    (/** @type {TableInstance} */ tableInstance) => {
      if (!managerTableFilters?.columnFilters) return;
      const updatedColumnFilters = managerTableFilters.columnFilters.map((cf) => ({
        ...cf,
        width: cf.visible ? `${Math.round(tableInstance.getColumn(cf.id)?.getSize())}px` : null,
      }));

      queryClient.setQueryData(
        [TABLE_FILTERS_QUERY_KEY, page?.path, contexte],
        (/** @type {ApiManagerTableFilters} */ old) => ({ ...old, columnFilters: updatedColumnFilters }),
      );

      mutateColumnResize(
        completeTableFiltersForPost({ columnFilters: updatedColumnFilters }, managerTableFilters),
      );
    },
    [managerTableFilters, queryClient, page?.path, contexte, mutateColumnResize],
  );

  // Domain - Table groups
  const {
    table,
    tableGroups,
    getGroupStatsValueByColumnId,

    // Table groups - Selected Rows
    selectedRows,
    selectedRowsCount,
    selectedRowsArray,
    handleSelectAllRows,
    allRowsAreSelected,
    selectAllRowsIndeterminate,
    handleSelectGroupRows,
    areAllGroupRowsSelected,
    isGroupRowsSelectionIndeterminate,
    handleSelectRowCallback,
    handleDeselectAndCloseBatchProcessing,
    activeCell,

    // Table groups - Batch Processing
    handleConfirmCloseBatchProcessing,
  } = useTableGroups({
    isLoading: isTableDataBlocking,
    columns: tableDataPath?.columns,
    groups: tableDataPath?.groups,
    columnsPinnedById,
    batchProcessingIsOpen,
    setBatchProcessingIsOpen,
    handleCloseBatchProcessing,
    // Columns leave room on the right for the row actions bar
    tableWrapperWidth: Math.max(0, tableWrapperWidth - ROW_ACTIONS_GUTTER_WIDTH),
    onColumnResizeEnd: handleColumnResizeEnd,
  });

  const virtualizedTableGroupsRef = useRef(null);
  const tableGroupVirtualCount = tableGroups.length;

  const groupVirtualizer = useVirtualizer({
    count: tableGroupVirtualCount,
    getScrollElement: () => virtualizedTableGroupsRef.current,
    estimateSize: () => 200,
    overscan: 1, // Render 1 extra groups above/below viewport
  });

  const tableFind = useTableFind({
    tableGroups,
    columns: tableDataPath?.columns,
    table,
    groupVirtualizer,
    isGroupAccordionOpen,
    toggleGroup,
  });

  // Mutation - Table Filters
  const {
    mutate: mutateTableFiltersOptimistic,
    isPending: isMutateTableFiltersOptimisticPending,
  } = useTableFiltersOptimistic(page?.path, {}, contexte);

  const {
    mutate: mutateTableFiltersOptimisticColumnManagement,
    isPending: isMutateTableFiltersOptimisticColumnManagementPending,
  } = useTableFiltersOptimisticColumnManagement(page?.path, {}, contexte);

  const {
    mutate: mutateTableFiltersGroupBy,
    isPending: isMutateTableFiltersGroupByPending,
  } = useTableFiltersOptimistic(page?.path, {}, contexte);

  const {
    mutate: mutateTableFiltersPersonalViews,
    isPending: isMutateTableFiltersPersonalViewsPending,
  } = useTableFiltersOptimistic(page?.path, {}, contexte);

  const isTableReloadMutationPending = isMutateTableFiltersOptimisticPending
    || isMutateTableFiltersGroupByPending
    || isMutateTableFiltersPersonalViewsPending
    || isResetViewPending
    || isGlobalFiltersMutating;

  const holdForFilterReload = useTableReloadSkeletonHold(
    isTableReloadMutationPending,
    isTableFetching,
  );

  const showTableDataSkeleton = shouldShowTableDataSkeleton({
    isTableDataBlocking,
    isPlaceholderData: isTablePlaceholderData,
    holdForFilterReload,
  });

  const handleColumnsManagementMutation = () => {
    mutateTableFiltersOptimisticColumnManagement(
      completeTableFiltersForPost({ columnFilters: columnFiltersLocal }, managerTableFilters),
      { onSuccess: () => setPopoverColumnsManagementIsOpen(false) },
    );
  };

  const handleSubmitFiltersMutation = (/** @type {ApiManagerTableFiltersSortByCriteriaValue[]} */ data) => {
    handleDeselectAndCloseBatchProcessing();
    resetPageToFirst();
    mutateTableFiltersOptimistic(
      completeTableFiltersForPost({
        sortBy: {
          id: managerTableFilters?.sortBy?.id,
          criteriaValues: data,
        },
      }, managerTableFilters),
      { onSuccess: () => tableSortBy.setPopoverSortByIsOpen(false) },
    );
  };

  const handleConditionalFiltersMutation = (/** @type {Array<ApiManagerFilterValues>} */ data) => {
    handleDeselectAndCloseBatchProcessing();
    resetPageToFirst();
    mutateTableFiltersOptimistic(
      completeTableFiltersForPost({ filterValues: data }, managerTableFilters),
      { onSuccess: () => conditionalFilters.setPopoverConditionalFiltersIsOpen(false) },
    );
  };

  const handleGroupBySelectedOptionMutation = (/** @type {string} */ selectedOption) => {
    handleDeselectAndCloseBatchProcessing();
    resetPageToFirst();
    mutateTableFiltersGroupBy(
      completeTableFiltersForPost({
        groupBy: { selectedOption },
      }, managerTableFilters),
      { onSuccess: () => setPopoverGroupByIsOpen(false) },
    );
  };

  const handlePersonalViewsSelectedOptionMutation = (/** @type {string} */ selectedOption) => {
    handleDeselectAndCloseBatchProcessing();
    resetPageToFirst();
    mutateTableFiltersPersonalViews(
      completeTableFiltersForPost({
        personalViews: { selectedOption },
      }, managerTableFilters),
      { onSuccess: () => setPopoverPersonalViewIsOpen(false) },
    );
  };

  const handleSearchMutation = () => {
    handleDeselectAndCloseBatchProcessing();
    resetPageToFirst();
    mutateTableFiltersOptimistic(
      completeTableFiltersForPost({
        search: {
          searchValue: tableSearch.tableSearch,
          selectedOptions: tableSearch.selectedColumnsArray,
        },
      }, managerTableFilters),
    );
  };

  const handlePaginationPageSizeMutation = (/** @type {string} */ size) => {
    setPageNumberFromPageSize(Number(size));
    mutateTableFiltersOptimistic(
      completeTableFiltersForPost({ pageSize: size.toString() }, managerTableFilters),
    );
  };

  // Mutation - Batch Processing
  const {
    mutate: mutateDeleteMultipleUrl,
    isPending: isDeleteMultipleUrlPending,
  } = useDeleteMultipleUrlMutation(managerTableFilters?.deleteMultipleUrl);

  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const handleOpenDeleteConfirmModal = () => {
    setIsDeleteConfirmOpen(true);
  };

  const handleCloseDeleteConfirmModal = () => {
    setIsDeleteConfirmOpen(false);
  };

  const confirmDeleteMultiple = () => {
    setIsDeleteConfirmOpen(false);

    openProgressModal(
      [{ id: 'delete', lib: t('sidePanel.delete') }],
      selectedRowsArray.length,
    );

    mutateDeleteMultipleUrl(
      {
        selectedRows: selectedRowsArray,
        onProgress: (payload) => updateActionProgress(0, payload),
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEY], type: 'active' });
          handleDeselectAndCloseBatchProcessing();
        },
      },
    );
  };

  const {
    mutate: mutateBatchSetAttribute,
    isPending: isBatchProcessingPending,
  } = useBatchSetAttributeMutation();

  const handleSubmitAttributeMultipleUrlMutation = (/** @type {RowBatchItem[]} */ data) => {
    const baseUrl = managerTableFilters?.setAttributeMultipleUrl;
    if (!baseUrl) return;
    const actions = data.map((item) => ({ id: item.id, value: item.value }));

    openProgressModal(data, selectedRowsArray.length);

    mutateBatchSetAttribute(
      {
        baseUrl,
        selectedRows: selectedRowsArray,
        actions,
        contexte,
        onProgress: (actionIndex, payload) => updateActionProgress(actionIndex, payload),
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEY], type: 'active' });
        },
      },
    );
  };

  const {
    mutate: mutateExport,
  } = useExportMutation({
    onSuccess: (data) => {
      window.open(data.url, '_blank');
    },
    onError: (error) => {
      notify({
        message: error?.message || t('toast.export.error', 'Une erreur est survenue lors de l\'export'),
        type: 'alert',
      });
    },
  });

  /**
   * @param {string} url - The URL to export
   */
  const handleExport = (url) => {
    mutateExport({ url, selectedIds: selectedRowsArray });
  };

  const hasSortFilterOptions = (managerTableFilters?.sortBy?.criteria?.length ?? 0) > 0;
  const hasGroupByFilterOptions = (managerTableFilters?.groupBy?.options?.length ?? 0) > 0;

  const isInitialLoading = isManagerTableFiltersLoading || isTableLoading || isWaitingForGlobalFilters;
  const isPageReady = !isInitialLoading && tableWrapperWidth > 0;
  const { remainingHeight } = useStickyTopElems(stickyTopElems, { enabled: isPageReady });
  const { stickyHeight: chromeHeight } = useStickyTopElems(['table-scroll-chrome'], { enabled: isPageReady });
  // In a detail tab, the wheel over the table first scrolls the page (hides the banner), then the table
  useSyncScrollWithWindow(virtualizedTableGroupsRef, isInTab && isPageReady);

  const tableContent = (
    <>
      <Helmet>
        <title>{managerTableFilters?.title ? `${managerTableFilters?.title} - Heeds Manager` : 'Heeds Manager'}</title>
      </Helmet>
      <div
        ref={virtualizedTableGroupsRef}
        className={tableViewStyles['table-page-scroll']}
        style={{ height: `${remainingHeight}px` }}
      >
        <div
          id="table-scroll-chrome"
          className={tableViewStyles['table-scroll-chrome']}
        >
          <TableTitle
            title={managerTableFilters?.title}
            subTitle={managerTableFilters?.subTitle}
            hideTitle={isInTab}
            className={!isMobileFiltersOpen ? tableTitleStyles['table-title-filters-collapsed'] : ''}
          >
            <span className={breakpointStyles['mobile-only']}>
              <button
                type="button"
                className={breakpointStyles['mobile-filter-toggle']}
                onClick={onMobileFiltersToggleClick}
                aria-expanded={isMobileFiltersOpen}
                aria-controls="table-filters"
              >
                <Picto
                  icon="chevron-down-range"
                  className={`${breakpointStyles['mobile-filter-toggle-icon']}${isMobileFiltersOpen ? ` ${breakpointStyles['mobile-filter-toggle-icon-open']}` : ''}`}
                  aria-hidden
                />
                <span>
                  {isMobileFiltersOpen
                    ? t('table.filters.hideMobile')
                    : t('table.filters.toggleMobile')}
                </span>
              </button>
            </span>
          </TableTitle>
          {tableFind.isOpen ? (
            <TableFindBar
              query={tableFind.query}
              onQueryChange={tableFind.setQuery}
              matchIndex={tableFind.currentMatchIndex}
              matchCount={tableFind.matches.length}
              onNext={tableFind.next}
              onPrev={tableFind.prev}
              onClose={tableFind.close}
            />
          ) : null}

          <TableFilters>
            <MobileCollapsible
              isOpen={isMobileFiltersOpen}
            >
              <TableFiltersToolbar>
                <TableFiltersGroup>
                  {/* Columns Management Popover */}
                  <Popover
                    open={popoverColumnsManagementIsOpen}
                    onOpenChange={setPopoverColumnsManagementIsOpen}
                    placement="bottom-start"
                  >
                    <PopoverTrigger asChild>
                      <ModalTrigger
                        icon="eye"
                        title={t('table.filters.columns')}
                        label={t('table.filters.columns')}
                        onClick={() => setPopoverColumnsManagementIsOpen(
                          !popoverColumnsManagementIsOpen,
                        )}
                        disabled={isMutateTableFiltersGroupByPending || isMutateTableFiltersPersonalViewsPending}
                      />
                    </PopoverTrigger>
                    <PopoverContent>
                      <FilterColumnManagement
                        columnManagement={columnManagement}
                        isMutateTableFiltersOptimisticPending={isMutateTableFiltersOptimisticColumnManagementPending}
                        handleColumnsManagementMutation={handleColumnsManagementMutation}
                      />
                    </PopoverContent>
                  </Popover>

                  {/* Table Filters Popover */}
                  {hasSortFilterOptions && (
                  <Popover
                    open={tableSortBy.popoverSortByIsOpen}
                    onOpenChange={tableSortBy.setPopoverSortByIsOpen}
                    placement="bottom-start"
                  >
                    <PopoverTrigger asChild>
                      <ModalTrigger
                        icon="sort"
                        title={t('table.filters.sort')}
                        label={t('table.filters.sort')}
                        onClick={() => tableSortBy.setPopoverSortByIsOpen(!tableSortBy.popoverSortByIsOpen)}
                        disabled={isMutateTableFiltersGroupByPending || isMutateTableFiltersPersonalViewsPending}
                      >
                        {tableSortBy.getFiltersCount(managerTableFilters?.sortBy?.criteriaValues) > 0 ? (
                          <Badge
                            value={tableSortBy.getFiltersCount(managerTableFilters?.sortBy?.criteriaValues)}
                          />
                        ) : null}
                      </ModalTrigger>
                    </PopoverTrigger>
                    <PopoverContent>
                      <SortByForm
                        isLoading={isManagerTableFiltersLoading}
                        isSubmitting={isMutateTableFiltersOptimisticPending}
                        onSubmit={handleSubmitFiltersMutation}
                        tableSortBy={tableSortBy}
                        sortBy={managerTableFilters?.sortBy}
                      />
                    </PopoverContent>
                  </Popover>
                  )}

                  {/* Conditional Filters Popover */}
                  <Popover
                    open={conditionalFilters.popoverConditionalFiltersIsOpen}
                    onOpenChange={conditionalFilters.setPopoverConditionalFiltersIsOpen}
                    placement="bottom-start"
                  >
                    <PopoverTrigger asChild>
                      <ModalTrigger
                        icon="filter"
                        title={t('table.filters.others')}
                        label={t('table.filters.others')}
                        onClick={() => conditionalFilters.setPopoverConditionalFiltersIsOpen(
                          !conditionalFilters.popoverConditionalFiltersIsOpen,
                        )}
                        maxWidth="150px"
                        isLoading={isManagerTableFiltersLoading}
                        disabled={isMutateTableFiltersGroupByPending || isMutateTableFiltersPersonalViewsPending}
                      >
                        {conditionalFilters.getConditionalFiltersCount(managerTableFilters?.filterValues) > 0
                          ? <Badge value={conditionalFilters.getConditionalFiltersCount(managerTableFilters?.filterValues)} />
                          : null}
                      </ModalTrigger>
                    </PopoverTrigger>
                    <PopoverContent>
                      <FilterConditionalFilters
                        isLoading={isManagerTableFiltersLoading}
                        isSubmitting={isMutateTableFiltersOptimisticPending}
                        onSubmit={(data) => handleConditionalFiltersMutation(data)}
                        conditionalFilters={conditionalFilters}
                      />
                    </PopoverContent>
                  </Popover>

                  {/* Groups By Popover */}
                  {hasGroupByFilterOptions && (
                  <Popover
                    open={popoverGroupByIsOpen}
                    onOpenChange={setPopoverGroupByIsOpen}
                    placement="bottom-start"
                  >
                    <PopoverTrigger asChild>
                      <ModalTrigger
                        icon={popoverGroupByIsOpen ? 'unfold-less' : 'unfold-more'}
                        iconPosition="right"
                        title={
                    managerTableFilters?.groupBy?.selectedOption
                      ? `${t('table.filters.groupByEvent')} ${groupByOptionsId[managerTableFilters?.groupBy?.selectedOption]?.lib}`
                      : t('table.filters.groupByEvent')
                  }
                        label={
                    managerTableFilters?.groupBy?.selectedOption
                      ? `${t('table.filters.groupByEvent')} ${groupByOptionsId[managerTableFilters?.groupBy?.selectedOption]?.lib}`
                      : t('table.filters.groupByEvent')
                  }
                        onClick={() => setPopoverGroupByIsOpen(!popoverGroupByIsOpen)}
                        maxWidth="240px"
                        disabled={isMutateTableFiltersPersonalViewsPending}
                      />
                    </PopoverTrigger>
                    <PopoverContent>
                      <CustomVueListGroup>
                        <CustomVueListGroupList>
                          <ColumnsVisibility
                            options={groupByOptionsGrouped}
                            messageNoOptions={t('table.filters.noResult')}
                          >
                            {(label, options) => (
                              <SearchGroupList
                                key={label}
                                hasLabel={!!label}
                                items={options}
                                label={label}
                              >
                                {(option) => (
                                  <SearchListItem
                                    key={option.id}
                                    id={option.id}
                                    theme="group"
                                    onClick={() => handleGroupBySelectedOptionMutation(option.id)}
                                    isSelected={managerTableFilters?.groupBy?.selectedOption === option.id}
                                  >
                                    {option.lib}
                                  </SearchListItem>
                                )}
                              </SearchGroupList>
                            )}
                          </ColumnsVisibility>
                        </CustomVueListGroupList>
                      </CustomVueListGroup>
                    </PopoverContent>
                  </Popover>
                  )}

                  {/* Personal Views Popover */}
                  <Popover
                    open={popoverPersonalViewIsOpen}
                    onOpenChange={setPopoverPersonalViewIsOpen}
                    placement="bottom-start"
                  >
                    <PopoverTrigger asChild>
                      <ModalTrigger
                        icon={popoverPersonalViewIsOpen ? 'unfold-less' : 'unfold-more'}
                        iconPosition="right"
                        title={selectedPersonalViewItem?.lib || t('table.filters.personalViews')}
                        label={selectedPersonalViewItem?.lib || t('table.filters.personalViews')}
                        onClick={() => setPopoverPersonalViewIsOpen(!popoverPersonalViewIsOpen)}
                        isLoading={isManagerTableFiltersLoading}
                        maxWidth="150px"
                        disabled={isMutateTableFiltersGroupByPending}
                      />
                    </PopoverTrigger>
                    <PopoverContent>
                      <FilterPersonalViews
                        mutationKey={TABLE_FILTERS_QUERY_KEY}
                        path={page?.path}
                        personalViews={personalViews}
                        personalViewsPath={managerTableFilters?.personalViewsPath}
                        personalViewsFormUrl={managerTableFilters?.personalViewsFormUrl}
                        handlePersonalViewsSelectedOptionMutation={handlePersonalViewsSelectedOptionMutation}
                        handleOpenModal={handleOpenModal}
                        managerPersonalViews={managerTableFilters?.personalViews}
                        contexte={contexte}
                        onResetView={() => resetView({ path: page?.path, type: 'tableFilters' })}
                        isResetting={isResetViewPending}
                      />
                    </PopoverContent>
                  </Popover>
                </TableFiltersGroup>

                {managerTableFilters?.search?.options && managerTableFilters?.search?.options.length > 0 && (
                <TableFiltersGroup>
                  {/* Search Filters Group */}
                  <FilterNavigationSearch
                    tableSearchDomain={tableSearch}
                    searchValue={managerTableFilters?.search?.searchValue}
                    options={managerTableFilters?.search?.options}
                    isLoading={isManagerTableFiltersLoading}
                    handleSearchMutation={handleSearchMutation}
                  />
                </TableFiltersGroup>
                )}
              </TableFiltersToolbar>
            </MobileCollapsible>
          </TableFilters>
        </div>

        {isTableError ? (
          <TableWrapper>
            <p className={styles['table-wrapper-error-message']}>
              {t('table.error')}
            </p>
          </TableWrapper>
        ) : (
          <TableColumnHoverProvider table={table}>
            <TableRowHoverProvider>
              <TableCellHoverPreviewProvider>
                <TableFindProvider
                  scrollTarget={tableFind.scrollTarget}
                  highlightedRowId={tableFind.highlightedRowId}
                  onScrollTargetConsumed={tableFind.clearScrollTarget}
                >
                  <TableWrapper isLoading={showTableDataSkeleton}>
                    <VirtualizedTableGroupsWithHoverLeave>
                      <VirtualizedStickyHeader top={chromeHeight}>
                        <Table width={table.getTotalSize()}>
                          <TableHead
                            table={table}
                            handleSelectAllRows={handleSelectAllRows}
                            allRowsAreSelected={allRowsAreSelected}
                            selectAllRowsIndeterminate={selectAllRowsIndeterminate}
                          />
                        </Table>
                        <div style={{ width: table.getTotalSize() }}>
                          <TableSubHeader table={table} />
                        </div>
                      </VirtualizedStickyHeader>

                      <VirtualizedTableGroup height={`${groupVirtualizer.getTotalSize()}px`}>
                        {groupVirtualizer.getVirtualItems().map((virtualRow) => {
                          const group = tableGroups[virtualRow.index];
                          return (
                            <TableGridGroup
                              key={virtualRow.key}
                              dataIndex={virtualRow.index}
                              measureRef={groupVirtualizer.measureElement}
                              start={virtualRow.start}
                              group={group}
                              table={table}
                              tableWidth={table.getTotalSize()}
                              isOpen={isGroupAccordionOpen[group.id] ?? true}
                              onToggleGroup={toggleGroup}
                              handleSelectGroupRows={handleSelectGroupRows}
                              isGroupSelected={areAllGroupRowsSelected(group)}
                              isGroupSelectionIndeterminate={isGroupRowsSelectionIndeterminate(group)}
                              handleSelectRow={handleSelectRowCallback}
                              selectedRows={selectedRows}
                              getGroupStatsValueByColumnId={getGroupStatsValueByColumnId}
                              activeCell={activeCell}
                              scrollElementRef={virtualizedTableGroupsRef}
                            />
                          );
                        })}
                      </VirtualizedTableGroup>
                    </VirtualizedTableGroupsWithHoverLeave>
                  </TableWrapper>
                </TableFindProvider>
                {showTableDataSkeleton ? null : (
                  <>
                    <TableRowHoverActions
                      containerRef={virtualizedTableGroupsRef}
                      disabled={batchProcessingIsOpen}
                      onOpenFormModal={handleOpenModal}
                    />
                    <CellHoverPreviewOverlay isCellEditing={Boolean(activeCell)} />
                  </>
                )}
              </TableCellHoverPreviewProvider>
            </TableRowHoverProvider>
          </TableColumnHoverProvider>
        )}
      </div>

      <BatchSidePanel
        isOpen={batchProcessingIsOpen}
        title={t('sidePanel.title', { count: selectedRowsCount })}
        onClose={() => handleConfirmCloseBatchProcessing()}
        closeOnEscape={!isBatchProgressModalOpen && !isDeleteConfirmOpen}
        footer={(
          <SidePanelFooter
            cancelAction={(
              <Button
                color="primary100"
                size="small"
                onClick={() => handleConfirmCloseBatchProcessing()}
              >
                {t('sidePanel.cancel')}
              </Button>
            )}
            applyAction={(
              <Button
                color="primary"
                size="small"
                onClick={handleSubmit((data) => handleSubmitAttributeMultipleUrlMutation(data.actions))}
                isLoading={isBatchProcessingPending}
              >
                {t('sidePanel.apply')}
              </Button>
            )}
          />
        )}
      >
        <RowBatchList
          control={control}
          name="actions"
          addButtonLabel={t('sidePanel.addAction')}
          createNewItem={handleCreateNewRowBatchItem}
        >
          {({
            field, index, remove, count, update,
          }) => (
            <RowBatchCard
              // eslint-disable-next-line no-underscore-dangle
              key={field._id}
              // eslint-disable-next-line no-underscore-dangle
              id={field._id}
              headerRight={(
                <IconButton
                  icon="trash-empty"
                  color="transparent"
                  size="medium"
                  onClick={remove}
                  title={t('sidePanel.removeAction')}
                  disabled={count === 1}
                />
              )}
            >
              <>
                <BatchActionSelect
                  control={control}
                  name={`actions[${index}].id`}
                  groups={transformedSetAttributeMultiple}
                  onChange={(value) => {
                    clearErrors(`actions.${index}.value`);
                    return update({ ...field, ...value });
                  }}
                  placeholder={t('sidePanel.selectAction')}
                  error={t(errors.actions?.[index]?.id?.message || '')}
                />
                <BatchActionRender
                  controlRhf={control}
                  name={`actions[${index}].value`}
                  field={field}
                  label={t('sidePanel.updateValue')}
                  type={field.type}
                  error={t(errors.actions?.[index]?.value?.message || '')}
                />
              </>
            </RowBatchCard>
          )}
        </RowBatchList>
      </BatchSidePanel>

      <TablePagination>
        <span className={breakpointStyles['desktop-only']}>
          <TablePaginationPerPage
            label={t('pagination.resultsTotal', {
              total: Number(tableDataPath?.pagination?.recordsCount ?? 0).toLocaleString(i18n.language),
            })}
            options={pageSizeOptions}
          >
            {(/** @type {{ label: string, value: string }} */ option) => (
              <TablePaginationPerPageButton
                key={option.value}
                value={option.value}
                isActive={option.value === managerTableFilters?.pageSize}
                onClick={handlePaginationPageSizeMutation}
              >
                {option.label}
              </TablePaginationPerPageButton>
            )}
          </TablePaginationPerPage>
        </span>

        <span className={breakpointStyles['mobile-only']}>
          <span className={tablePaginationLayoutStyles['table-pagination-mobile-per-page']}>
            <FilterSelect
              className={filterSelectStyles['wrapper-inline-width']}
              options={pageSizeOptions}
              value={managerTableFilters?.pageSize}
              handleValue={handlePaginationPageSizeMutation}
              hasPrevNext={false}
              isLoading={isManagerTableFiltersLoading}
              disabled={isMutateTableFiltersOptimisticPending}
              ariaLabel={t('pagination.pageSizeSelect')}
              template="table"
            >
              {(/** @type {{ label: string, value: string }} */ option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              )}
            </FilterSelect>
            <span className={tablePaginationPerPageStyles['table-pagination-per-page-label']}>
              {t('pagination.resultsTotal', {
                total: Number(tableDataPath?.pagination?.recordsCount ?? 0).toLocaleString(i18n.language),
              })}
            </span>
          </span>
        </span>

        <TablePaginationNavigation>
          {managerTableFilters?.pageSize !== 'none' && (tableDataPath?.pagination?.pagesCount ?? 0) > 1 && (
            <TablePaginationPages
              currentPage={pageNumber}
              totalPages={tableDataPath?.pagination?.pagesCount ?? 1}
              onPageChange={(p) => goToPage(p, tableDataPath?.pagination?.pagesCount ?? 1)}
              isLoading={isTableFetching || isMutateTableFiltersOptimisticPending}
            />
          )}
        </TablePaginationNavigation>
      </TablePagination>

      <DeleteRowsConfirmModal
        id="modal-delete-multiple-confirm"
        isOpen={isDeleteConfirmOpen}
        onClose={handleCloseDeleteConfirmModal}
        onConfirm={confirmDeleteMultiple}
        count={selectedRowsArray.length}
        isLoading={isDeleteMultipleUrlPending}
      />

      <Modal
        id="modal-batch-progress"
        isOpen={isBatchProgressModalOpen}
        onClose={closeProgressModal}
        className={modalStyles['modal-panel-large']}
        header={(
          <ModalHeader
            title={t('sidePanel.progressModalTitle')}
            onClose={closeProgressModal}
          />
        )}
      >
        {batchProgressActionItems.map((item, i) => {
          const state = actionProgressList[i];
          const completed = state?.completed ?? 0;
          const total = state?.total ?? 0;
          const errorCount = state?.errorCount ?? 0;
          const messages = state?.messages ?? [];
          const openTab = state?.openTab ?? null;
          return (
            <BatchActionProgressionContainer
              key={i} // eslint-disable-line react/no-array-index-key -- stable list; items can share id
              title={item.lib}
              description={t('sidePanel.linesProcessed', { completed, errorCount })}
              messages={messages}
              action={openTab ? (
                <Button
                  size="small"
                  color="primary"
                  onClick={() => window.open(openTab.basicUrl, '_blank')}
                >
                  {openTab.lib}
                </Button>
              ) : null}
              progressIndicator={<CircleProgressIndicator completed={completed} total={total} hasErrors={errorCount > 0} />}
            />
          );
        })}
        <div style={{ marginTop: 'var(--spacing-m)', display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            color="primary100"
            size="small"
            onClick={closeProgressModal}
          >
            {t('sidePanel.quit')}
          </Button>
        </div>
      </Modal>

      <FloatingMenu className={floatingMenuStyles['above-table-pagination']}>
        <FloatingMenuGroups key={selectedRowsCount ? 'selection' : 'default'}>
          {selectedRowsCount ? (
            <FloatingMenuGroup>
              <SelectionCount
                count={selectedRowsCount}
                label={t('table.selectedCount', { count: selectedRowsCount })}
              />
            </FloatingMenuGroup>
          ) : null}
          <FloatingMenuGroup>
            <Popover
              open={popoverExportIsOpen}
              onOpenChange={setPopoverExportIsOpen}
              placement="top"
            >
              <PopoverTrigger>
                <ResponsiveActionButton
                  color={selectedRowsCount ? 'primary' : 'no-background'}
                  size="small"
                  iconPosition="right"
                  icon="export"
                  ariaLabel={t('table.export')}
                  label={t('table.export')}
                  onClick={() => setPopoverExportIsOpen(!popoverExportIsOpen)}
                  disabled={batchProcessingIsOpen}
                />
              </PopoverTrigger>
              <PopoverContent>
                <CustomVueListGroup variant="wide">
                  <CustomVueListGroupList>
                    <CustomVueListGroupTitle>Exporter</CustomVueListGroupTitle>
                    {optGroupsList?.length ? (
                      <HorizontalList items={optGroupsList} gap="xs" className="margin-top-s margin-bottom-s">
                        {(option) => {
                          const isSelected = selectedFormatFilter === option;
                          return (
                            <Button
                              key={option.url}
                              color={isSelected ? 'primary' : 'primary100'}
                              size="small"
                              onClick={() => {
                                setSelectedFormatFilter(isSelected ? null : option);
                              }}
                            >
                              {option}
                            </Button>
                          );
                        }}
                      </HorizontalList>
                    ) : (
                      <p>{t('table.filters.export.noResult')}</p>
                    )}
                    <ColumnsVisibility
                      search={(
                        <SearchField
                          id="table-export-search"
                          placeholder={t('table.filters.columnsGroupsSearch')}
                          value={exportSearchValue}
                          onChange={(e) => setExportSearchValue(e.target.value)}
                          onFocus={() => {}}
                        />
                      )}
                      options={exportUrlsGrouped}
                      messageNoOptions={t('table.filters.noResult')}
                    >
                      {(key, items) => (
                        <SearchGroupList
                          key={key}
                          hasLabel={!!key}
                          items={items}
                          label={key}
                        >
                          {(/** @type {TableExportUrlsWithId} */ item) => (
                            <SearchListItem
                              key={item.id}
                              id={item.id}
                              theme="group"
                              onClick={() => handleExport(item.url)}
                              isSelected={false}
                            >
                              <LabelWithIconAndColor
                                label={item.lib}
                                icon={item.icon}
                                iconColor={item.iconColor}
                              />
                            </SearchListItem>
                          )}
                        </SearchGroupList>
                      )}
                    </ColumnsVisibility>
                  </CustomVueListGroupList>
                </CustomVueListGroup>
              </PopoverContent>
            </Popover>
            {tableData?.importUrls?.length && !selectedRowsCount ? (
              <Popover
                open={popoverImportIsOpen}
                onOpenChange={setPopoverImportIsOpen}
                placement="top"
              >
                <PopoverTrigger>
                  <ResponsiveActionButton
                    color="no-background"
                    size="small"
                    iconPosition="right"
                    icon="import"
                    ariaLabel={t('table.import')}
                    label={t('table.import')}
                    onClick={handleImportClick}
                  />
                </PopoverTrigger>
                <PopoverContent>
                  <CustomVueListGroup variant="wide">
                    <CustomVueListGroupList>
                      <CustomVueListGroupTitle>{t('table.import')}</CustomVueListGroupTitle>
                      <ColumnsVisibility
                        search={(
                          <SearchField
                            id="table-import-search"
                            placeholder={t('table.filters.columnsGroupsSearch')}
                            value={importSearchValue}
                            onChange={(e) => setImportSearchValue(e.target.value)}
                            onFocus={() => {}}
                          />
                        )}
                        options={importUrlsGrouped}
                        messageNoOptions={t('table.filters.noResult')}
                      >
                        {(key, items) => (
                          <SearchGroupList
                            key={key}
                            hasLabel={!!key}
                            items={items}
                            label={key}
                          >
                            {(/** @type {TableImportUrlsWithId} */ item) => (
                              <SearchListItem
                                key={item.id}
                                id={item.id}
                                theme="group"
                                onClick={() => {
                                  handleOpenModal(item.url);
                                  setPopoverImportIsOpen(false);
                                }}
                                isSelected={false}
                              >
                                <LabelWithIconAndColor
                                  label={item.lib}
                                  icon={item.icon}
                                  iconColor={item.iconColor}
                                />
                              </SearchListItem>
                            )}
                          </SearchGroupList>
                        )}
                      </ColumnsVisibility>
                    </CustomVueListGroupList>
                  </CustomVueListGroup>
                </PopoverContent>
              </Popover>
            ) : null}
            {selectedRowsCount && managerTableFilters?.deleteMultipleUrl ? (
              <ResponsiveActionButton
                color="primary"
                size="small"
                ariaLabel={t('sidePanel.delete')}
                label={t('sidePanel.delete')}
                onClick={() => handleOpenDeleteConfirmModal()}
                iconPosition="right"
                icon="trash-empty"
                disabled={batchProcessingIsOpen || isDeleteMultipleUrlPending}
                isLoading={isDeleteMultipleUrlPending}
              />
            ) : null}
            {selectedRowsCount ? (
              <>
                {managerTableFilters?.actionMultiple?.map((action) => (
                  <Button
                    key={action.id}
                    color="primary"
                    size="small"
                    disabled={batchProcessingIsOpen}
                    onClick={() => handleOpenModal(appendSelectedIdsToUrl(action.url, selectedRowsArray))}
                  >
                    <LabelWithIconAndColor
                      label={action.lib}
                      icon={action.icon}
                      iconColor={action.iconColor}
                      labelColor="var(--neutral-00)"
                      iconPosition="right"
                    />
                  </Button>
                ))}
                {managerTableFilters?.setAttributeMultipleUrl ? (
                  <ResponsiveActionButton
                    color="primary"
                    size="small"
                    icon="edit-2"
                    iconPosition="right"
                    ariaLabel={t('table.modifyBatch')}
                    label={t('table.modifyBatch')}
                    onClick={() => setBatchProcessingIsOpen(true)}
                    disabled={batchProcessingIsOpen}
                  />
                ) : null}
              </>
            ) : null}
            {!selectedRowsCount && tableData?.addFormUrls?.length ? (
              <Popover
                open={popoverAddElementIsOpen}
                onOpenChange={setPopoverAddElementIsOpen}
                placement="top"
              >
                <PopoverTrigger>
                  <ResponsiveActionButton
                    color="primary"
                    size="small"
                    icon="add"
                    iconPosition="left"
                    desktopPresentation="labelOnly"
                    ariaLabel={t('table.addElement')}
                    label={t('table.addElement')}
                    onClick={handleAddElementClick}
                  />
                </PopoverTrigger>
                <PopoverContent>
                  <CustomVueListGroup>
                    <CustomVueListGroupList>
                      <CustomVueListGroupTitle>Ajouter un élément</CustomVueListGroupTitle>
                      <ColumnsVisibility
                        search={(
                          <SearchField
                            id="table-add-element-search"
                            placeholder={t('table.filters.columnsGroupsSearch')}
                            value={addElementSearchValue}
                            onChange={(e) => setAddElementSearchValue(e.target.value)}
                            onFocus={() => {}}
                            className="margin-top-s"
                          />
                        )}
                        options={addFormUrlsGrouped}
                        messageNoOptions={t('table.filters.noResult')}
                      >
                        {(key, items) => (
                          <SearchGroupList
                            key={key}
                            hasLabel={!!key}
                            items={items}
                            label={key}
                          >
                            {(/** @type {AddFormUrlWithId} */ item) => (
                              <SearchListItem
                                key={item.id}
                                id={item.id}
                                theme="group"
                                onClick={() => {
                                  handleOpenModal(item.url);
                                  setPopoverAddElementIsOpen(false);
                                }}
                                isSelected={false}
                              >
                                <LabelWithIconAndColor
                                  label={item.lib}
                                  icon={item.icon}
                                  iconColor={item.iconColor}
                                  hasColorSquareFallback
                                />
                              </SearchListItem>
                            )}
                          </SearchGroupList>
                        )}
                      </ColumnsVisibility>
                    </CustomVueListGroupList>
                  </CustomVueListGroup>
                </PopoverContent>
              </Popover>
            ) : null}
          </FloatingMenuGroup>
        </FloatingMenuGroups>
      </FloatingMenu>
    </>
  );

  return (
    <>
      <div
        ref={tableWidthProbeRef}
        className={styles['table-wrapper']}
        style={{
          position: 'absolute',
          height: 0,
          visibility: 'hidden',
          pointerEvents: 'none',
          overflow: 'hidden',
        }}
        aria-hidden="true"
      />
      {isPageReady ? tableContent : <CircleLoader centered />}
    </>
  );
}

export default TableView;
